import { useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { IconEdit, IconArchive } from '@tabler/icons-react';
import { AppTable, ArchiveRestoreModal } from 'components/organisms';
import { PersonListDisplay, Filter } from 'components/molecules';
import { groupDTOFields } from 'consts/group/groupDTOFields';
import { GroupDTO, GroupId } from 'types';
import { useIsMobile } from 'hooks/useIsMobile/useIsMobile';
import { appPaths } from 'consts/paths/paths';
import { Option } from 'components/atoms';
import { commonTexts, groupsTexts, journalTexts } from 'consts/text';
import { useGroupAbilities, useJournalAbilities } from 'abilities';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import dayjs from 'dayjs';
import { usePageFilters } from 'hooks/usePageFilters/usePageFilters';
import { parseFilterParamsIntoGetGroupsFiltersValues } from 'utils/parsers/group/parseFilterParamsIntoGetGroupsFiltersValues';
import { isBeforeSchoolYear } from 'utils/isBeforeSchoolYear';
import { useGroupsPageNetworkManage } from './hooks/useGroupsPageNetworkManage';
import { filters, getColumns } from './GroupsPage.utils';

export const GroupsPage = () => {
    const [selectedToArchiveGroupId, setSelectedToArchiveGroupId] = useState<GroupId | null>(null);
    const isMobile = useIsMobile();
    const { statefulNavigate } = useNavigateWithState();
    const { groupAbilities } = useGroupAbilities();
    const {
        genericAbilities: { checkIfCanArchiveJournals },
    } = useJournalAbilities();

    const { filtersParams, searchFilterDebounceValue, handleChangeFilters, handleSearchInput } =
        usePageFilters({ filters });

    const canManageGroupList = groupAbilities.checkIfCanManageGroupList();
    // JOURNALS_ARCHIVING is a permission that is used for both journals and groups
    const canArchiveGroups = checkIfCanArchiveJournals();

    const onGroupArchiveRequestFinish = () => setSelectedToArchiveGroupId(null);

    const filtersValues = parseFilterParamsIntoGetGroupsFiltersValues(
        filtersParams,
        searchFilterDebounceValue,
    );

    const currentDate = dayjs();
    const currentYear = currentDate.year();
    const isBeforeNewSchoolYear = isBeforeSchoolYear(currentDate, currentYear);

    const [fromYearDefaultValue, toYearDefaultValue] = isBeforeNewSchoolYear
        ? [currentYear - 1, currentYear]
        : [currentYear, currentYear + 1];
    const fromYearFilterValue = filtersValues.fromYear
        ? filtersValues.fromYear
        : fromYearDefaultValue;
    const toYearFilterValue = filtersValues.toYear ? filtersValues.toYear : toYearDefaultValue;

    const {
        groupsData,
        isGroupsDataInitialLoading,
        isGroupsDataRefetching,
        isGroupsDataError,
        isArchiveGroupLoading,
        archiveGroup,
    } = useGroupsPageNetworkManage({
        filtersValues: {
            ...filtersValues,
            fromYear: fromYearFilterValue,
            toYear: toYearFilterValue,
        },
        onGroupArchiveRequestFinish,
    });

    const handleArchiveGroupClick = (group: GroupDTO) => setSelectedToArchiveGroupId(group.id);

    const handleArchiveGroupConfirm = () => {
        if (!selectedToArchiveGroupId) {
            return;
        }
        archiveGroup(selectedToArchiveGroupId);
    };

    const handleArchiveGroupCancel = () => setSelectedToArchiveGroupId(null);

    const navigateToGroupAdd = () => statefulNavigate(appPaths.app.groups.add);

    const expandedRowRender = useCallback(
        (group: GroupDTO) => (
            <PersonListDisplay
                personsData={group[groupDTOFields.childrenArray]}
                desktopLabel="Obecne dzieci"
            />
        ),
        [],
    );

    const expandable = useMemo(
        () =>
            !isMobile
                ? {
                      expandedRowRender,
                      showExpandColumn: true,
                  }
                : undefined,
        [isMobile, expandedRowRender],
    );

    const columns = useMemo(() => getColumns(filtersValues), [filtersValues]);

    const options: Option<GroupDTO>[] = [
        {
            label: commonTexts.actionLabels.edit,
            onClick: ({ id }) =>
                statefulNavigate(
                    generatePath(appPaths.app.groups.edit.pickedGroup, { groupId: id }),
                ),
            checkIfVisible: () => canManageGroupList,
            Icon: <IconEdit />,
        },
        {
            label: commonTexts.actionLabels.archive,
            onClick: handleArchiveGroupClick,
            checkIfVisible: (group: GroupDTO) => canArchiveGroups && group.status !== 'ARCHIVED',
            Icon: <IconArchive />,
        },
    ];

    return (
        <>
            <ArchiveRestoreModal
                open={!!selectedToArchiveGroupId}
                title={groupsTexts.archiveGroupModalTitle}
                confirmActionLoading={isArchiveGroupLoading}
                onOk={handleArchiveGroupConfirm}
                onCancel={handleArchiveGroupCancel}
            />
            <AppTable
                title="Lista grup"
                addButtonLabel={groupsTexts.addNewGroup}
                emptyInfo="Nie masz utworzonych żadnych grup"
                options={options}
                columns={columns}
                rowKey={({ id }) => id}
                dataSource={groupsData}
                expandable={expandable}
                isDataInitialLoading={isGroupsDataInitialLoading}
                isDataRefetching={isGroupsDataRefetching}
                isOptionsLoading={isArchiveGroupLoading}
                isError={isGroupsDataError}
                isHiddenAddButton={!canManageGroupList}
                onAddButtonClick={navigateToGroupAdd}
                tableManageUtilsProps={{
                    searchInputProps: {
                        defaultValue: searchFilterDebounceValue,
                        onInput: handleSearchInput,
                    },
                    additionalFilters: (
                        <Filter
                            input={
                                <Filter.RangePicker
                                    picker="year"
                                    value={[
                                        dayjs().year(fromYearFilterValue),
                                        dayjs().year(toYearFilterValue),
                                    ]}
                                    allowClear={false}
                                    onChange={(_, dateStrings) =>
                                        handleChangeFilters({
                                            fromYear: dateStrings[0],
                                            toYear: dateStrings[1],
                                        })
                                    }
                                />
                            }
                            label={journalTexts.journals.filters.schoolYear}
                            width="20%"
                        />
                    ),
                }}
            />
        </>
    );
};
